import {
  MuiTelInput,
  type MuiTelInputCountry,
  type MuiTelInputInfo,
  type MuiTelInputProps,
} from 'mui-tel-input';
import { forwardRef, useCallback, useEffect } from 'react';
import { styles } from './styles.ts';
import { Box, FormHelperTextProps, SxProps } from '@mui/material';
import { Theme } from '@mui/system';
import { getExampleNumber } from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';
import { flagUrls } from '~/helpers/flags.ts';

export type PhoneFieldInputProps = Omit<
  MuiTelInputProps,
  'country' | 'inputProps' | 'localization' | 'onChange'
> & {
  dataTestId?: string;
  errorDataTestId?: string;
  isTableInput?: boolean;
  allowedCountries?: MuiTelInputCountry[];
  onChange: (value: string) => void;
};

const PhoneField = forwardRef<HTMLDivElement, PhoneFieldInputProps>(
  (
    {
      onChange,
      name,
      dataTestId,
      allowedCountries,
      defaultCountry = 'UA',
      isTableInput = false,
      errorDataTestId,
      ...props
    },
    ref
  ) => {
    const inputId = name ?? dataTestId ?? 'phone-field';

    const maxNationalLength = (country?: MuiTelInputCountry) =>
      (country && getExampleNumber(country, examples)?.nationalNumber.length) ?? 15;

    const handlePhoneChange = useCallback(
      (newValue: string, info: MuiTelInputInfo) => {
        if (info.reason === 'country') {
          onChange(`+${info.countryCallingCode}`);
          return;
        }

        const next = info.numberValue ?? newValue.replace(/[^\d+]/g, '');

        if ((info.nationalNumber?.length ?? 0) > maxNationalLength(info.countryCode ?? undefined)) {
          return;
        }

        onChange(next);
      },
      [onChange]
    );
    useEffect(() => {
      const handlePointerDown = (event: PointerEvent) => {
        const paper = document.querySelector<HTMLElement>('.MuiTelInput-Menu .MuiMenu-paper');
        if (!paper) return;

        const target = event.target as Element | null;
        if (paper.contains(target) || target?.closest('.MuiTelInput-IconButton')) return;

        paper.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
      };

      document.addEventListener('pointerdown', handlePointerDown, true);
      return () => document.removeEventListener('pointerdown', handlePointerDown, true);
    }, []);

    return (
      <MuiTelInput
        {...props}
        fullWidth
        onChange={handlePhoneChange}
        inputRef={ref}
        getFlagElement={(isoCode, { imgProps, countryName }) => {
          const src = flagUrls[isoCode];
          return src ? (
            <Box component="img" {...imgProps} src={src} alt="" />
          ) : (
            <Box component="span" aria-label={countryName}>
              🏳️
            </Box>
          );
        }}
        placeholder="+X (XX) XXX-XX-XX"
        forceCallingCode={false}
        onlyCountries={allowedCountries}
        langOfCountryName="uk"
        defaultCountry={defaultCountry}
        sx={[styles.root, props.sx] as SxProps<Theme>}
        MenuProps={{
          disableScrollLock: true,
          marginThreshold: null,
          transitionDuration: 120,
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
          hideBackdrop: true,
          disableEnforceFocus: true,
          disableRestoreFocus: true,
          slotProps: {
            root: { sx: { pointerEvents: 'none' } },
            paper: { sx: styles.paper },
          },
        }}
        slotProps={{
          ...props.slotProps,
          htmlInput: {
            ...props.slotProps?.htmlInput,
            id: inputId,
            name: name,
            'data-testid': dataTestId ? `${dataTestId}-input` : 'phoneField-input',
          },
          root: { sx: { pointerEvents: 'none' } },
          formHelperText: { 'data-testid': errorDataTestId } as FormHelperTextProps & {
            'data-testid': string;
          },
        }}
      />
    );
  }
);

PhoneField.displayName = 'PhoneField';

export default PhoneField;
